/**
 * Subscription Page
 * Subscription plans and payment for providers
 */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useLanguage, useAuth, API } from "@/context/AppContext";
import { Check, CheckCircle, AlertCircle, Loader2, Gift, Tag, Lock, Calendar, XCircle, RefreshCw } from "lucide-react";

const SubscriptionPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  // If we are in the "Add role" flow, the URL contains ?role=client|provider
  // and that role's pricing should be shown (regardless of the user's
  // current active role).
  const urlRole = new URLSearchParams(location.search).get('role');
  const targetRole = (urlRole || user?.active_role || user?.role || 'client').toLowerCase();
  const isProvider = targetRole === 'provider';
  const planTitle = isProvider ? (t('expertPlanTitle') || 'Experte') : (t('clientPlanTitle') || 'Klient');
  const planDesc = isProvider ? (t('expertPlanDesc') || 'Für Dienstleister') : (t('clientPlanDesc') || 'Für Kunden, die Experten suchen');
  const planPrice = isProvider ? 179 : 19;
  const planMonthly = isProvider ? '14.90' : '1.58';
  const planFeatures = isProvider
    ? [t('expertF1') || 'Unbegrenzte Bewerbungen', t('expertF2') || 'Verifiziertes Profil', t('expertF3') || 'Priorität in der Suche', t('expertF4') || 'Direkte Kommunikation']
    : ['Unbegrenzte Anfragen', 'Direkter Chat mit Experten', 'Termin-Buchung', 'Nur verifizierte Profis'];
  const [loading, setLoading] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [saferpayConfigured, setSaferpayConfigured] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  
  // Check if user was redirected due to blocked access
  const blockedState = location.state;
  const isBlocked = blockedState?.blocked;
  const blockStatus = blockedState?.status;
  const blockReason = blockedState?.reason;

  const fetchStatus = () => {
    axios.get(`${API}/subscriptions/status`, { withCredentials: true })
      .then(res => setSubStatus(res.data))
      .catch(console.error);
  };

  useEffect(() => {
    fetchStatus();
    axios.get(`${API}/saferpay/status`)
      .then(res => setSaferpayConfigured(res.data.configured))
      .catch(console.error);
  }, []);

  const handleCancel = async () => {
    if (!window.confirm("Möchten Sie Ihr Abonnement wirklich kündigen? Sie behalten Zugang bis zum Ende der bezahlten Periode.")) return;
    setCancelLoading(true);
    try {
      const role = user?.active_role || user?.role;
      await axios.post(`${API}/subscriptions/cancel`, { role }, { withCredentials: true });
      toast.success("Abonnement gekündigt");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kündigung fehlgeschlagen");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleToggleAutoRenew = async (enabled) => {
    try {
      const role = user?.active_role || user?.role;
      await axios.post(`${API}/account/auto-renew`, { role, enabled }, { withCredentials: true });
      toast.success(enabled ? "Auto-Verlängerung aktiviert" : "Auto-Verlängerung deaktiviert");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Konnte Auto-Verlängerung nicht ändern");
    }
  };

  const handleReactivate = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${API}/subscriptions/reactivate`, {}, { withCredentials: true });
      toast.success("Abonnement reaktiviert");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reaktivierung fehlgeschlagen");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Bitte geben Sie einen Promo-Code ein");
      return;
    }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/apply`, { code: promoCode.trim() }, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
        setSubStatus({ subscription: "promo", expires: res.data.expires });
        setPromoCode("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Promo-Code konnte nicht angewendet werden");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(plan);
    try {
      // If user is going through the add-role flow, target the role specified in the URL
      const params = new URLSearchParams(location.search);
      const targetRole = params.get('role');
      const body = { plan_type: plan };
      if (targetRole) body.role = targetRole;
      const response = await axios.post(`${API}/saferpay/subscription`, body, { withCredentials: true });
      
      if (response.data.success && response.data.redirect_url) {
        localStorage.setItem('pending_payment_token', response.data.token);
        localStorage.setItem('pending_payment_plan', plan);
        window.location.href = response.data.redirect_url;
      } else if (!response.data.configured) {
        toast.info("Saferpay nicht konfiguriert - Verwende Test-Modus");
        const res = await axios.post(`${API}/subscriptions`, { plan }, { withCredentials: true });
        toast.success("Abo aktiviert!");
        setSubStatus(res.data);
      }
    } catch (error) {
      if (error.response?.status === 400 && !saferpayConfigured) {
        try {
          const res = await axios.post(`${API}/subscriptions`, { plan }, { withCredentials: true });
          toast.success("Abo aktiviert! (Test-Modus)");
          setSubStatus(res.data);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Fehler");
        }
      } else {
        toast.error(error.response?.data?.detail || "Zahlung fehlgeschlagen");
      }
    } finally {
      setLoading(null);
    }
  };

  const subType = subStatus?.subscription;
  const showPromo = !subType;

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('subscription')}
        </h1>
        <p className="text-gray-500 mt-1">{t('manageSubscription')}</p>
      </div>

      {/* Blocked access warning banner */}
      {isBlocked && (
        <Card className="border-red-400 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Lock className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-bold text-lg text-red-700">
                  Zugriff eingeschränkt
                </p>
                <p className="text-red-600 mt-1">
                  {blockStatus === 'pending' && 'Sie haben noch kein aktives Abonnement. Bitte wählen Sie einen Plan, um fortzufahren.'}
                  {blockStatus === 'expired' && 'Ihr Abonnement ist abgelaufen. Bitte erneuern Sie Ihr Abo, um wieder Zugriff zu erhalten.'}
                  {blockStatus === 'pending_renewal' && 'Ihre Zahlung konnte nicht verarbeitet werden. Bitte aktualisieren Sie Ihre Zahlungsmethode.'}
                  {blockStatus === 'expired_trial' && 'Ihre Testphase ist abgelaufen. Wählen Sie einen Plan, um weiter Zugang zu haben.'}
                  {!blockStatus && (blockReason || 'Bitte wählen Sie ein Abonnement, um fortzufahren.')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!saferpayConfigured && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                Saferpay nicht konfiguriert - Zahlungen werden im Test-Modus simuliert
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {subType && (!urlRole || urlRole === (user?.active_role || user?.role)) && (
        <Card className="border-[#0044CC] bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-1 min-w-[240px]">
                <CheckCircle className="w-8 h-8 text-[#0044CC] flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">
                    {subType === 'monthly' ? t('monthly') : subType === 'annual' ? t('annual') : 'Promo'} {t('subscriptionActive')}
                  </p>
                  {subStatus?.expires && (
                    <p className="text-gray-600 text-sm flex items-center gap-1 mt-1">
                      <Calendar className="w-4 h-4" />
                      {t('validUntil')}: {new Date(subStatus.expires).toLocaleDateString('de-CH')}
                      {typeof subStatus?.days_remaining === 'number' && (
                        <span className="ml-2 text-[#0044CC] font-semibold">
                          ({subStatus.days_remaining} {subStatus.days_remaining === 1 ? 'Tag' : 'Tage'})
                        </span>
                      )}
                    </p>
                  )}
                  {subStatus?.subscription_status === 'cancelled' && (
                    <Badge className="mt-2 bg-orange-100 text-orange-800 border border-orange-300">
                      Gekündigt – läuft am Enddatum aus
                    </Badge>
                  )}
                  {subStatus?.subscription_status !== 'cancelled' && (
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                        subStatus?.auto_renew ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                      }`} data-testid="auto-renew-status">
                        <RefreshCw className="w-3 h-3" />
                        {subStatus?.auto_renew ? 'Auto-Verlängerung AN' : 'Auto-Verlängerung AUS'}
                      </span>
                      {subStatus?.has_alias && (
                        <button
                          onClick={() => handleToggleAutoRenew(!subStatus?.auto_renew)}
                          className="text-xs underline text-[#0044CC] hover:text-[#003399]"
                          data-testid="auto-renew-toggle-btn"
                        >
                          {subStatus?.auto_renew ? 'deaktivieren' : 'aktivieren'}
                        </button>
                      )}
                      {!subStatus?.has_alias && (
                        <span className="text-gray-400 text-[11px]">(keine gespeicherte Karte)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                {subStatus?.subscription_status === 'cancelled' ? (
                  <Button
                    onClick={handleReactivate}
                    disabled={cancelLoading}
                    className="bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
                    data-testid="reactivate-subscription-btn"
                  >
                    {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><RefreshCw className="w-4 h-4 mr-2" />Reaktivieren</>)}
                  </Button>
                ) : subStatus?.can_cancel ? (
                  <Button
                    onClick={handleCancel}
                    disabled={cancelLoading}
                    variant="outline"
                    className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="cancel-subscription-btn"
                  >
                    {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><XCircle className="w-4 h-4 mr-2" />Kündigen</>)}
                  </Button>
                ) : subStatus?.cancel_window_blocked ? (
                  <div className="text-xs text-gray-500 max-w-[220px] text-right">
                    Kündigung nur möglich, wenn mehr als 7 Tage bis zur Verlängerung verbleiben.
                  </div>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {subStatus?.subscription_status === 'expired' && !subType && (
        <Card className="border-orange-400 bg-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <AlertCircle className="w-8 h-8 text-orange-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-lg text-orange-800">Abonnement pausiert</p>
                <p className="text-orange-700 text-sm mt-1">
                  Ihr Abonnement ist abgelaufen. Erneuern Sie es, um wieder vollen Zugang zu erhalten.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {showPromo && (
        <Card className="border-purple-300 bg-purple-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Gift className="w-6 h-6 text-purple-600" />
              <h3 className="font-bold text-lg text-purple-800">Promo-Code einloesen</h3>
            </div>
            <div className="flex gap-3">
              <Input
                placeholder="Promo-Code eingeben"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className="flex-1"
                data-testid="promo-code-input"
              />
              <Button
                onClick={handleApplyPromo}
                disabled={promoLoading || !promoCode.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="apply-promo-btn"
              >
                {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>Einloesen</span>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 max-w-md mx-auto">
        <Card className="border-[#0044CC] relative">
          <div className="absolute top-4 right-4">
            <Badge className="bg-[#0044CC] text-white">PRO</Badge>
          </div>
          <CardHeader className="text-center">
            <CardTitle>{planTitle}</CardTitle>
            <CardDescription>{planDesc}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="mb-6">
              <span className="text-4xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>CHF {planPrice}</span>
              <span className="text-gray-500">{t('perYear')}</span>
              <p className="text-sm text-gray-500 mt-1">≈ CHF {planMonthly}{t('perMonth')}</p>
            </div>
            <ul className="space-y-3 text-left">
              {planFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
              onClick={() => handleSubscribe('annual')}
              disabled={loading === 'annual' || (subType === 'annual' && (!urlRole || urlRole === (user?.active_role || user?.role)))}
              data-testid="subscribe-annual-btn"
            >
              {loading === 'annual' ? (
                <span className="flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verarbeitung...</span>
              ) : (subType === 'annual' && (!urlRole || urlRole === (user?.active_role || user?.role))) ? t('currentPlan') : t('saveNow')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionPage;
